import { EventAvailable, Unarchive } from "@mui/icons-material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import Snooze from "@mui/icons-material/Snooze";
import {
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Popover,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersShortcutsItem } from "@mui/x-date-pickers/PickersShortcuts";
import { addDays, isPast, isToday, startOfDay } from "date-fns";
import { sv } from "date-fns/locale";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useState } from "react";
import {
	Assignment,
	AssignmentStatus,
	archivePersonAssignment,
	deletePersonAssignment,
	getAllPersonAssignments,
	getAssignments,
	getPersonAssignments,
	PersonAssignmentRetrieveType,
	setPersonAssignmentStatus,
	snoozePersonAssignment,
} from "../../api";
import { getAssignmentBoardConfigs } from "../../api/assignments/assignment-board-config";
import { useJobylonLinkedJobs } from "../../hooks/use-jobylon-linked-jobs";
import { formatDate, formatRelativeTime } from "../../utils/date-formatting";
import { BoardColumnType, Status } from "../boardv2/board-layout-config";
import DeletePersonDialog from "../boardv2/delete-person-dialog";
import { ViewParamKeys } from "../boardv2/utils";
import { useGlobalState } from "../globalState";
import { AddToRoleDialog } from "./add-to-role-dialog";
import { JobylonStageCell } from "./jobylon-stage-cell";

const SHORTCUT_ITEMS: PickersShortcutsItem<Date>[] = [
	{ label: "Tomorrow", getValue: () => addDays(startOfDay(new Date()), 1) },
	{ label: "In 1 week", getValue: () => addDays(startOfDay(new Date()), 7) },
	{ label: "In 2 weeks", getValue: () => addDays(startOfDay(new Date()), 14) },
	{ label: "In 1 month", getValue: () => addDays(startOfDay(new Date()), 30) },
	{ label: "In 3 months", getValue: () => addDays(startOfDay(new Date()), 90) },
];

const PersonAssignmentStatusChip = ({
	pa,
	onStatusChange,
}: {
	pa: PersonAssignmentRetrieveType;
	onStatusChange: () => void;
}) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const [currentAssignmentId, setCurrentAssignmentId] = useState<number>(null);
	const [assignmentConfig, setAssignmentConfig] = useState(null);
	const { currentUser } = useGlobalState();
	const [personAssignmentId, setPersonAssignmentId] = useQueryState(
		ViewParamKeys.PERSON_ASSIGNMENT_ID,
		parseAsString
	);

	useEffect(() => {
		if (currentAssignmentId) {
			getAssignmentBoardConfigs(currentAssignmentId).then((res) => {
				setAssignmentConfig(res.results[0]);
			});
		}
	}, [currentAssignmentId]);

	const handleMenuOpen = (event: any) => {
		setAnchorEl(event.currentTarget);
		setCurrentAssignmentId(pa.assignment);
	};
	const handleClose = () => {
		setAnchorEl(null);
		setCurrentAssignmentId(null);
	};

	const handleUpdateStatus = (column: BoardColumnType, status: Status) => {
		if (pa.status_str === status.name) return;

		setPersonAssignmentStatus({
			newStatus: status,
			currentUser: currentUser,
			itemToUpdate: pa,
			column: column,
		}).then((res) => {
			// If the person assignment id is the same as the one we just updated, then we need to update the search params
			if (personAssignmentId === pa.id.toString()) {
				setPersonAssignmentId(res.newPersonAssignment.id.toString(), {
					history: "replace",
				});
			}
			onStatusChange();
			handleClose();
		});
	};
	return (
		<>
			<Typography
				variant="body2"
				sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
				onClick={(e) => handleMenuOpen(e)}
			>
				{pa.column}
				{pa.column !== pa.status_str ? ` / ${pa.status_str}` : ""}
			</Typography>
			<Popover
				id="simple-menu"
				anchorEl={anchorEl}
				keepMounted
				onClose={handleClose}
				open={Boolean(anchorEl)}
				sx={{
					"& .MuiPaper-root": {
						padding: 0,
					},
					height: 300,
				}}
			>
				{assignmentConfig?.config?.columns.map((column, idx) => {
					return (
						<Stack key={`${currentAssignmentId}_${column}_${idx}`}>
							<Typography
								sx={{
									fontWeight: "bold",
									p: 1,
								}}
							>
								{column.name}
							</Typography>
							<Grid container spacing={1} sx={{ p: 1, maxWidth: 400 }}>
								{column.statuses.map((status, idx) => {
									return (
										<Grid
											key={`${currentAssignmentId}_${status.name}_${idx}`}
										>
											<Chip
												variant={
													status.name === pa.status_str
														? "outlined"
														: "filled"
												}
												label={status.name}
												onClick={() =>
													handleUpdateStatus(column, status)
												}
											/>
										</Grid>
									);
								})}
							</Grid>
						</Stack>
					);
				})}
			</Popover>
		</>
	);
};

const PersonAssignmentSnoozeButton = ({
	pa,
	onSnooze,
}: {
	pa: PersonAssignmentRetrieveType;
	onSnooze: () => void;
}) => {
	const [snoozeDate, setSnoozeDate] = useState<Date>(
		pa?.snoozed === null ? addDays(startOfDay(new Date()), 7) : new Date(pa.snoozed)
	);
	const [dialogOpen, setDialogOpen] = useState(false);
	const { currentUser } = useGlobalState();

	const handleOpenDialog = () => {
		setSnoozeDate(
			pa?.snoozed === null
				? addDays(startOfDay(new Date()), 7)
				: new Date(pa.snoozed)
		);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
	};

	const handleSaveSnooze = () => {
		snoozePersonAssignment({
			snoozed: snoozeDate.toISOString(),
			currentUser,
			itemToUpdate: pa,
		}).then(() => {
			onSnooze();
			setDialogOpen(false);
		});
	};

	const shouldDisableDate = (date: Date) => {
		return !isToday(date) && isPast(date);
	};

	let icon = <CircularProgress />;
	let title = "";

	if (pa.snoozed === null) {
		icon = <EventAvailable />;
		title = "No snooze - click to set";
	} else if (new Date(pa.snoozed) <= new Date()) {
		icon = <Snooze color="error" />;
		title = `Expired ${formatRelativeTime(pa?.snoozed)} ago (${formatDate(pa?.snoozed)})`;
	} else if (new Date(pa.snoozed) > new Date()) {
		icon = <Snooze />;
		title = `Snoozed until ${formatRelativeTime(pa?.snoozed)} from now (${formatDate(pa?.snoozed)})`;
	}

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sv}>
			<Tooltip title={title}>
				<IconButton onClick={handleOpenDialog} size="small">
					{icon}
				</IconButton>
			</Tooltip>
			<Dialog
				open={dialogOpen}
				onClose={handleCloseDialog}
				maxWidth="xs"
				data-component="snooze-person-assignment-dialog"
			>
				<DialogTitle>Snooze Person</DialogTitle>
				<DialogContent>
					<StaticDatePicker
						value={snoozeDate}
						onChange={(newValue) =>
							newValue instanceof Date && setSnoozeDate(newValue)
						}
						shouldDisableDate={shouldDisableDate}
						slotProps={{
							shortcuts: { items: SHORTCUT_ITEMS },
							actionBar: { actions: [] },
						}}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Cancel</Button>
					<Button variant="contained" onClick={handleSaveSnooze}>
						Snooze
					</Button>
				</DialogActions>
			</Dialog>
		</LocalizationProvider>
	);
};

export const PersonAssignments = ({
	personId,
	disableDeletion = false,
	roleAddedCounter,
}: {
	personId: number;
	disableDeletion?: boolean;
	roleAddedCounter?: number;
}) => {
	const [assignments, setAssignments] = useState<Assignment[]>([]);
	const [personAssignments, setPersonAssignments] = useState<
		PersonAssignmentRetrieveType[]
	>([]);
	const [isLoadingAssignments, setIsLoadingAssignments] = useState<boolean>(false);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const { currentUser } = useGlobalState();
	// Fetched once here rather than per row, so a person on twenty roles still
	// asks for the Jobylon map a single time.
	const jobylonByAssignment = useJobylonLinkedJobs();
	const [open, setOpen] = useState<PersonAssignmentRetrieveType>(null);

	useEffect(() => {
		fetchPersonAssignments();
	}, []);

	useEffect(() => {
		if (roleAddedCounter) {
			fetchPersonAssignments();
		}
	}, [roleAddedCounter]);

	useEffect(() => {
		// If there are existing personAssignments, we need to fetch assignments to display them
		if (personAssignments.length > 0 && assignments.length === 0) {
			fetchAssignments();
		}
	}, [personAssignments]);

	const fetchAssignments = () => {
		if (assignments.length === 0) {
			setIsLoadingAssignments(true);
			getAssignments()
				.then(setAssignments)
				.finally(() => setIsLoadingAssignments(false));
		}
	};

	const fetchPersonAssignments = () => {
		getPersonAssignments({ personIds: [personId] }).then((results) => {
			setPersonAssignments(results);
		});
	};

	const handleClickOpenDialog = (pa: PersonAssignmentRetrieveType) => {
		setOpen(pa);
	};

	const handleCloseDialog = () => {
		setOpen(null);
	};

	const handleDeletePersonFromAssignment = () => {
		getAllPersonAssignments({
			personId: open.person,
			assignmentId: open.assignment,
		}).then((res) => {
			const personAssignments = res.results;
			let promises = [];
			for (let pa of personAssignments) {
				let promise = deletePersonAssignment(pa.id);
				promises.push(promise);
			}
			Promise.all(promises).then(() => {
				fetchPersonAssignments();
				handleCloseDialog();
			});
		});
	};

	const handleUnArchivePersonAssignment = (
		pa: PersonAssignmentRetrieveType,
		archived = false
	) => {
		archivePersonAssignment({
			archived: archived,
			itemToUpdate: pa,
			currentUser: currentUser,
		}).then(() => {
			fetchPersonAssignments();
		});
	};

	return (
		<>
			<TableContainer data-component="roles-table">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Company</TableCell>
							<TableCell>Role</TableCell>
							<TableCell>Role Status</TableCell>
							<TableCell>Person Status</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoadingAssignments && personAssignments.length > 0 ? (
							<TableRow data-component="loading-person-assignments">
								<TableCell colSpan={5}>
									<Stack
										direction="row"
										spacing={2}
										alignItems="center"
										sx={{ py: 1 }}
									>
										<CircularProgress size={20} />
										<Typography>Loading role details...</Typography>
									</Stack>
								</TableCell>
							</TableRow>
						) : (
							personAssignments.map((pa) => {
								const assignment = assignments.find(
									(a) => a.id === pa.assignment
								);

								return (
									<TableRow
										key={pa.id}
										data-component="person-assignment-row"
										sx={{
											...(assignment?.status !==
												AssignmentStatus.ACTIVE && {
												opacity: 0.6,
											}),
										}}
									>
										<TableCell>
											{assignment?.company_name}
										</TableCell>
										<TableCell>{assignment?.name}</TableCell>
										<TableCell>
											{assignment?.status_display}
										</TableCell>
										<TableCell>
											{pa.archived === true ? (
												<Typography
													variant="body2"
													color="warning.main"
												>
													Archived
												</Typography>
											) : pa.jobylon_stage != null &&
												jobylonByAssignment.has(
													pa.assignment
												) ? (
												// Jobylon holds an application for this person
												// here. Someone on a Jobylon role who was
												// never sent there keeps ODI's own chip.
												<JobylonStageCell
													pa={pa}
													jobylonJobId={
														jobylonByAssignment.get(
															pa.assignment
														).id
													}
													jobylonBoardUrl={
														jobylonByAssignment.get(
															pa.assignment
														).board_url
													}
													onMoved={fetchPersonAssignments}
												/>
											) : (
												<PersonAssignmentStatusChip
													pa={pa}
													onStatusChange={
														fetchPersonAssignments
													}
												/>
											)}
										</TableCell>
										<TableCell align="right">
											<Stack
												direction="row"
												spacing={0.5}
												justifyContent="flex-end"
											>
												{!disableDeletion &&
													pa.archived !== true && (
														<PersonAssignmentSnoozeButton
															pa={pa}
															onSnooze={
																fetchPersonAssignments
															}
														/>
													)}
												{!disableDeletion &&
													pa.archived !== true && (
														<Tooltip title="Delete from role">
															<IconButton
																onClick={() =>
																	handleClickOpenDialog(
																		pa
																	)
																}
																size="small"
															>
																<DeleteForeverIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
												{!disableDeletion && pa.archived && (
													<Tooltip title="Unarchive">
														<IconButton
															onClick={() =>
																handleUnArchivePersonAssignment(
																	pa
																)
															}
															size="small"
														>
															<Unarchive
																color="warning"
																fontSize="small"
															/>
														</IconButton>
													</Tooltip>
												)}
											</Stack>
										</TableCell>
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<Stack
				direction="row"
				justifyContent="flex-end"
				alignItems="center"
				spacing={1}
				sx={{ mt: 2 }}
				data-component="add-role-footer"
			>
				<Button
					variant="outlined"
					size="small"
					startIcon={<AssignmentIcon />}
					onClick={() => setAddDialogOpen(true)}
					data-component="add-to-role-button"
				>
					Add to Board
				</Button>
			</Stack>

			<AddToRoleDialog
				personId={personId}
				open={addDialogOpen}
				onClose={() => setAddDialogOpen(false)}
				onAdded={fetchPersonAssignments}
			/>
			<DeletePersonDialog
				open={open !== null}
				onClose={handleCloseDialog}
				onDelete={handleDeletePersonFromAssignment}
			/>
		</>
	);
};
